import React from 'react';
import { Box, Typography, Grid, Divider } from '@mui/material';

/**
 * RentalDetails Component
 * Displays a consistent rental pricing breakdown across the application
 * 
 * @param {Object} props
 * @param {Object} props.priceDetails - Pricing information from API
 * @param {number} props.priceDetails.days - Number of rental days
 * @param {number} props.priceDetails.daily_rate - Daily rate in cents
 * @param {number} props.priceDetails.base_charge - Total base charge in cents
 * @param {number} props.priceDetails.taxes - Total taxes in cents
 * @param {number} props.priceDetails.taxes_per_reservation - Per-item taxes in cents
 * @param {number} props.priceDetails.amount - Total amount in cents
 * @param {number} props.priceDetails.discount_amount - Discount amount in cents
 * @param {Array} props.priceDetails.surcharges - Array of surcharge objects
 * @param {number} props.selectedQuantity - Number of vehicles selected (default: 1)
 * @param {Date|string} props.selectedStartDate - Rental start date (Date object or formatted string)
 * @param {Date|string} props.selectedEndDate - Rental end date (Date object or formatted string)
 * @param {number} props.couponPercent - Coupon discount percentage (0-100)
 * @param {boolean} props.isMobile - Whether to use mobile styling
 * @param {string} props.noteContent - Optional note content to display
 * @param {string} props.confirmationId - Optional confirmation ID (checkout only)
 * @param {string} props.vehicleName - Optional vehicle name (checkout only)
 * @param {boolean} props.free - When true, suppress ALL monetary rows (base charge, rate, taxes,
 *                               discount, surcharges, Total) and the day-count Duration line.
 *                               Used for unmonetized (free) reservations on the confirmation page.
 * @param {string} props.timeWindow - Optional time-of-day range (e.g. "9:00 AM – 5:00 PM"). When
 *                               provided (free flow), shown as "Time" instead of the day-count Duration.
 */
const RentalDetails = ({
  priceDetails,
  selectedQuantity = 1,
  selectedStartDate,
  selectedEndDate,
  couponPercent = 0,
  isMobile = false,
  noteContent = null,
  confirmationId = null,
  createdDate = null,
  vehicleName = null,
  free = false,
  timeWindow = null
}) => {
  if (!priceDetails) {
    return null;
  }

  // ACOS: label the pricing in the reservation's increment unit (hourly vs daily).
  // min_increment_minutes + units come from the price API; fall back to days for legacy/daily.
  const isHourly = priceDetails.min_increment_minutes && Number(priceDetails.min_increment_minutes) < 1440;
  const unitCount = priceDetails.units != null ? priceDetails.units : priceDetails.days;
  const unitLabel = isHourly ? (unitCount === 1 ? 'hour' : 'hours') : (unitCount === 1 ? 'day' : 'days');
  const rateLabel = isHourly ? 'Hourly Rate' : 'Daily Rate';
  const perUnit = isHourly ? '/hr' : '/day';

  // Helper to format dates - handles both Date objects and strings
  const formatDateDisplay = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date; // Already formatted
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Reservation creation timestamp -> date + time down to the minute (checkout confirmation only).
  const formatCreatedDisplay = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };
  const createdDisplay = formatCreatedDisplay(createdDate);

  return (
    <Box
      sx={{
        textAlign: 'left',
        width: '100%',
        background: 'rgba(0,184,148,0.1)',
        padding: isMobile ? '12px' : '16px',
        borderRadius: '8px',
        marginBottom: '16px',
        boxSizing: 'border-box' // Include padding in width calculation
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: isMobile ? 18 : 22,
          fontWeight: 600,
          color: '#00B894',
          marginBottom: '12px',
          textAlign: 'center'
        }}
      >
        Rental Details
      </Typography>
      
      <Grid container spacing={1}>
        {/* Confirmation ID (checkout only) */}
        {confirmationId && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                Confirmation ID:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {confirmationId}
              </Typography>
            </Grid>
          </>
        )}

        {/* Created timestamp (checkout only) */}
        {createdDisplay && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                Created:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {createdDisplay}
              </Typography>
            </Grid>
          </>
        )}

        {/* Vehicle Name (checkout only) */}
        {vehicleName && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                Vehicle Type:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {vehicleName}
              </Typography>
            </Grid>
          </>
        )}

        {/* Duration — for free/unmonetized reservations, show the time window (if known)
            instead of a bogus day count; omit entirely when no time window is available. */}
        {free ? (
          timeWindow && (
            <>
              <Grid item xs={7}>
                <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                  Time:
                </Typography>
              </Grid>
              <Grid item xs={5} sx={{ textAlign: 'right' }}>
                <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                  {timeWindow}
                </Typography>
              </Grid>
            </>
          )
        ) : (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                Duration:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {unitCount} {unitLabel}
              </Typography>
            </Grid>
          </>
        )}

        {/* Quantity - Only show if more than 1 */}
        {selectedQuantity > 1 && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                Quantity:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {selectedQuantity} {selectedQuantity === 1 ? 'vehicle' : 'vehicles'}
              </Typography>
            </Grid>
          </>
        )}
        
        {/* Start Date */}
        {selectedStartDate && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                Start Date:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {formatDateDisplay(selectedStartDate)}
              </Typography>
            </Grid>
          </>
        )}
        
        {/* End Date */}
        {selectedEndDate && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                End Date:
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                {formatDateDisplay(selectedEndDate)}
              </Typography>
            </Grid>
          </>
        )}
        
        {/* All monetary rows are suppressed for free/unmonetized reservations. */}
        {!free && (
          <>
        {/* Daily Rate - Full width informational text */}
        {priceDetails.daily_rate && (
          <Grid item xs={12}>
            <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500, color: '#666', fontStyle: 'italic' }}>
              {rateLabel}{selectedQuantity > 1 ? ' (per vehicle)' : ''}: ${(priceDetails.daily_rate / 100).toFixed(2)}{perUnit}
            </Typography>
          </Grid>
        )}
        
        {/* Base Charge */}
        <Grid item xs={7}>
          <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
            {selectedQuantity > 1 ? (
              <>Base Charge ({unitCount} {unitLabel} × ${(priceDetails.daily_rate / 100).toFixed(2)}{perUnit} × {selectedQuantity}):</>
            ) : (
              <>Base Charge ({unitCount} {unitLabel} × ${(priceDetails.daily_rate / 100).toFixed(2)}{perUnit}):</>
            )}
          </Typography>
        </Grid>
        <Grid item xs={5} sx={{ textAlign: 'right' }}>
          <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
            ${(priceDetails.base_charge / 100).toFixed(2)}
          </Typography>
        </Grid>
        
        {/* Discount */}
        {couponPercent > 0 && priceDetails.discount_amount > 0 && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500, color: '#00B894' }}>
                Discount ({couponPercent}%):
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, color: '#00B894' }}>
                -${(priceDetails.discount_amount / 100).toFixed(2)}
              </Typography>
            </Grid>
          </>
        )}
        
        {/* Surcharges/Add-ons */}
        {priceDetails.surcharges && priceDetails.surcharges.map((surcharge, index) => {
          // Only show quantity multiplier for amount-based surcharges (not percent-based)
          const isAmountBased = surcharge.amount && !surcharge.percent;
          const perItemAmount = (isAmountBased && selectedQuantity > 1) 
            ? (surcharge.calculated_amount / selectedQuantity) 
            : surcharge.calculated_amount;
          
          return surcharge.calculated_amount > 0 && (
            <React.Fragment key={index}>
              <Grid item xs={7}>
                <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                  {(isAmountBased && selectedQuantity > 1) ? (
                    <>{surcharge.description} (${(perItemAmount / 100).toFixed(2)} × {selectedQuantity}):</>
                  ) : (
                    <>{surcharge.description}:</>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={5} sx={{ textAlign: 'right' }}>
                <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                  ${(surcharge.calculated_amount / 100).toFixed(2)}
                </Typography>
              </Grid>
            </React.Fragment>
          );
        })}
        
        {/* Taxes */}
        {priceDetails.taxes > 0 && (
          <>
            <Grid item xs={7}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 500 }}>
                {selectedQuantity > 1 ? (
                  <>Taxes (${(priceDetails.taxes_per_reservation / 100).toFixed(2)} × {selectedQuantity}):</>
                ) : (
                  <>Taxes:</>
                )}
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16 }}>
                ${(priceDetails.taxes / 100).toFixed(2)}
              </Typography>
            </Grid>
          </>
        )}
                      
        {/* Divider before total */}
        <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
        
        {/* Total */}
        <Grid item xs={7}>
          <Typography component="div" sx={{ fontSize: isMobile ? 16 : 18, fontWeight: 600 }}>
            Total:
          </Typography>
        </Grid>
        <Grid item xs={5} sx={{ textAlign: 'right' }}>
          <Typography component="div" sx={{ fontSize: isMobile ? 16 : 18, fontWeight: 600 }}>
            ${parseFloat(priceDetails.amount).toFixed(2)}
          </Typography>
        </Grid>
          </>
        )}
      </Grid>

      {/* Optional Note Display */}
      {noteContent && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mt: 1 }}>
            <Typography component="div" sx={{ fontSize: isMobile ? 14 : 16, fontWeight: 600 }}>
              Note:
            </Typography>
            <Typography 
              component="div" 
              sx={{ 
                fontSize: isMobile ? 14 : 16,
                mt: 1,
                p: 1,
                backgroundColor: 'rgba(0,0,0,0.03)',
                borderRadius: '4px',
                border: '1px solid rgba(0,0,0,0.09)'
              }}
            >
              {noteContent}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RentalDetails;
